import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ChartOptions, TooltipItem } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import {
  ChartData,
  ChartDataPoint,
  ChartDataSet,
  DataPoint,
} from '../chart/chart.component';
import * as _adapter from 'chartjs-adapter-moment';
import { RelativeTimePipe } from 'src/app/pipes/RelativeTimePipe';
import {DecimalPipe} from "@angular/common";

declare global {
  interface String {
    toPlural(count: number): string;
  }
}

String.prototype.toPlural = function (this: string, count: number) {
  var s = this;
  if (count == 1) return s;
  if (s.endsWith('s')) return s;
  return `${s}s`;
};

@Component({
  selector: 'pla-metric-graph',
  templateUrl: './metric-graph.component.html',
  styleUrls: ['./metric-graph.component.scss'],
})
export class MetricGraphComponent implements OnInit, AfterViewInit {
  @ViewChild(BaseChartDirective, { static: true })
  chartElement: BaseChartDirective;
  public gradient: CanvasGradient;

  public chartData: ChartData;
  public chartOptions: ChartOptions;

  @Input() public chartType: 'line' | 'bar' = 'line';
  @Input() public rgb: number[] = [50, 123, 141];
  @Input() public key: string = '';
  @Input() public dataPoints: DataPoint[] = [];
  @Input() public icon: string;
  @Input() public title: string;
  @Input() public interaction: string;
  @Input() public description: string;
  @Input() public ordinalOptions: { current: string, previous: string, relativeTime: boolean } = { current: 'today', previous: 'yesterday', relativeTime: true };

  public all: number;
  public today: DataPoint;
  public yesterday: DataPoint;
  public todayAnalytic: number;
  public containerId: string;
  public noData: boolean = false;

  constructor(private relativeTimePipe: RelativeTimePipe, private numberPipe: DecimalPipe) {
    this.containerId = this.makeid(4);
  }

  private makeid(length): string {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
  }

  ngOnInit(): void {
    window.onresize = (_: any) => {
      if (!this.chartElement) return;
      if (!this.chartElement.chart) return;
      this.chartElement.chart.resize();
    };

    if (this.dataPoints.length === 0) {
      this.noData = true;
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.createGradient();
      this.buildChartOptions();
      this.createChartData();
      // this.buildMultiChartData()
    }, 100);
  }

  public CreateRGB(important: boolean = true, opacity: number = 1): string {
    return `rgba(${this.rgb.join(',')},${opacity})`;
  }

  private getOrCreateTooltip(chartContainer: HTMLElement): HTMLElement {
    // Find or create the tooltip element
    let tooltipEl = document.getElementById(`${this.containerId}-custom-tooltip`);
    if (!tooltipEl) {
      tooltipEl = document.createElement('div');
      tooltipEl.id = `${this.containerId}-custom-tooltip`;
      tooltipEl.style.position = 'absolute';
      tooltipEl.style.border = '1px solid #ccc';
      tooltipEl.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
      tooltipEl.style.pointerEvents = 'none';
      tooltipEl.style.zIndex = '9999';
      tooltipEl.style.transition = 'all .1s ease';
      chartContainer.appendChild(tooltipEl);
    }

    tooltipEl.style.opacity = '1';
    return tooltipEl
  }

  private customTooltip(that: this, payload: any) {
    const tooltipModel = payload.tooltip.$context.tooltipItems[0];
    const chartContainer = document.getElementById(`chart-${this.key}-${that.containerId}`);
    const tooltip = payload.tooltip;

    const tooltipEl = that.getOrCreateTooltip(chartContainer);
    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = '0';
      return;
    }

    if (tooltipModel === undefined) return;
    const chartCanvas = chartContainer.getElementsByTagName('canvas')[0];
    const tooltipData = tooltipModel.raw;

    // Custom tooltip HTML content
    // 2592000 -> 1 MONTH max 'relative'
    const __html = `
        <div class="card p-2 bg-dark text-light" style="min-width: 100px;">
            <p class="mb-0 text-center fs-6 text-light lh-1 mb-1">
                ${
                  this.chartType === 'bar' ?
                  tooltipData.x :
                  this.ordinalOptions.relativeTime ? that.relativeTimePipe.transform(new Date(tooltipData.x), true, 2592000) : new Date(tooltipData.x).toLocaleDateString()
                }
            </p>
            <p class="mb-0 d-flex justify-content-center align-items-center">
                <span class="me-2 small text-light material-icons" style="line-height: 0.9">${that.icon}</span>
                <span class="text-light">${this.numberPipe.transform(tooltipData.y)}</span>
            </p>
        </div>
    `;

    // Set the tooltip content
    tooltipEl.innerHTML = __html;
    const { offsetLeft: positionX, offsetTop: positionY } = chartCanvas;
    tooltipEl.style.left = positionX + tooltip.caretX + 'px';
    tooltipEl.style.top = positionY + tooltip.caretY + 'px';


    const x = Number.parseFloat(tooltipEl.style.left);
    tooltipEl.style.transform = `translate(${x < (chartCanvas.width / 2) ? '0' : '-100%'}, -105%)`;
    // console.log(tooltipEl.style.left, chartCanvas.width);
  }

  private buildChartOptions(): void {
    var that = this;
    const yOptions = {
      //type: 'logarithmic',
      // type: 'linear',
      min: this.chartType === 'bar' ? 0 : -1,
      display: false,
      grid: {
        display: false,
        borderColor: 'rgba(0,0,0,1)',
      },
      stacked: false,
      beginAtZero: true,
      ticks: {
        display: false,
        // stepSize: 1,
        color: 'rgba(0,0,0,1)',
        maxRotation: 0,
        minRotation: 0,
      },
    }
    this.chartOptions = {
      responsive: true,
      resizeDelay: 200,
      maintainAspectRatio: false,
      layout: {},
      plugins: {
        tooltip: {
          enabled: false,
          external: (_: any) => this.customTooltip(this, _),
          callbacks: {
            title: function (tooltipItems: TooltipItem<any>[]): string {
              return `${that.title} on ${tooltipItems[0].label.substring(
                0,
                12
              )}`;
            },
          },
        },
      },
      interaction: {
        intersect: false,
        mode: 'nearest',
        axis: 'xy',
      },
      elements: {
        line: {
          borderWidth: 2,
          tension: 0.25,
        },
      },
      datasets: {
        [this.chartType]: {
          fill: this.chartType === 'line'
            ? { target: 'start', above: this.createGradient() }
            : false,
          backgroundColor: this.chartType === 'bar' ? this.createGradient(true) : 'transparent',//'rgba(50,123,141,1)',
          hoverBackgroundColor: this.CreateRGB(false, 1),//'rgba(50,123,141,1)',
          borderColor: this.CreateRGB(false),//'rgba(50,123,141,1)',
          pointBackgroundColor: this.CreateRGB(false),//'rgba(50,123,141,1)',
          pointBorderColor: this.CreateRGB(false),//'rgba(50,123,141,1)',
          pointHoverBackgroundColor: 'rgba(0,0,0,1)',
          pointHoverBorderColor: 'rgba(0,0,0,1)',
          pointRadius: 0,
        },
      },
      scales: {
        x: {
          display: this.chartType === 'bar' ? true : false,
          type: this.chartType === 'bar' ? 'category' : 'time',
          weight: 0,
          adapters: {
            date: _adapter,
          },
          ticks: {
            display: this.chartType === 'bar' ? true : false,
            font: {
              size: 12,
            },
            align: 'center',
            maxRotation: 0,
            minRotation: 0,
            maxTicksLimit: 20,
          },
          time: {
            unit: 'day',
            displayFormats: {
              quarter: 'DD MMM',
            },
          },
          grid: {
            lineWidth: 0,
            display: false,
            borderColor: 'rgba(0,0,0,1)',
          },
        },
        y: {
          ...yOptions,
          position: 'left',
          grid: { display: false },
          max: this.calcMaxY(),
          ticks: {...yOptions.ticks, stepSize: this.chartType === 'bar' ? 1 : this.clamp(Math.round(this.calcMaxY() / 70), 1, 50) }
        },
      },
    };
  }

  private createGradient(subtle: boolean = false): CanvasGradient {
    const r = this.rgb[0];
    const g = this.rgb[1];
    const b = this.rgb[2];

    if (this.gradient) return this.gradient;
    if (this.chartElement == null || this.chartElement.chart == null) return this.gradient;
    var ctx = this.chartElement.chart.canvas.getContext('2d');
    var gradient = ctx.createLinearGradient(
      0,
      this.chartElement.chart.height,
      0,
      0
    );
    gradient.addColorStop(1, `rgba(${r},${g},${b},1)`);
    gradient.addColorStop(0.1, `rgba(${r},${g},${b},${ subtle ? 0.5 : 0.05 })`);
    gradient.addColorStop(0, `rgba(${r},${g},${b},${ subtle ? 0.1 : 0.01 })`);
    return gradient;
  }

  private createChartData(): void {
    if (this.noData) {
      const dataClass = new ChartDataSet([
        new ChartDataPoint(new Date(new Date().getTime()), 0)
      ]);
      this.chartData = new ChartData([dataClass]);
      return;
    }

    if (this.chartType === 'bar') {
      const dataClass = new ChartDataSet(this.dataPoints);
      this.chartData = new ChartData([dataClass]);
      console.log(this.title, this.chartData, this.calcMaxY());
      return;
    }

    this.all = 0;
    var chartDataPoints: ChartDataPoint[] = [];
    var p = this.dataPoints.sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime()).reverse();
    p.forEach((dp: DataPoint) => {
      var _x = new Date(dp.x);
      _x.setHours(0, 0, 0, 0);
      chartDataPoints.push(new ChartDataPoint(_x, dp.y));
      this.all += dp.y;
    });

    var dataClass = new ChartDataSet(chartDataPoints);
    this.chartData = new ChartData([dataClass]);

    p = [...this.dataPoints].sort((a, b) => a.x - b.x).reverse();
    this.today = p[p.length - 1];
    this.yesterday = p[p.length - 2];
    this.todayAnalytic =
      Math.ceil(((this.today.y - this.yesterday.y) / this.yesterday.y) * 100) ||
      0;
    if (!isFinite(this.todayAnalytic)) this.todayAnalytic = 100;
  }

  private clamp = (num, min, max) => {
    return Math.min(Math.max(num, min), max);
  };

  private calcMaxY(): number {
    if (this.noData) return 10;
    const sorted = [...this.dataPoints]
      .sort(
        (a, b) => a.y - b.y)
      .reverse();
    const highestRecord = sorted[0];
    const lowestRecord = sorted[sorted.length - 1];

    var calcY = highestRecord.y;
    calcY = Math.round(this.clamp(calcY * 1.5, 1, calcY + highestRecord.y - lowestRecord.y)); // adds 10% onto max value, max 100 extra

    if (calcY < 10) return 10;
    return calcY;
  }
}
